// /admin/tenant — Ring App Store workspace (tenant) settings.
//
// Display + edit display_name and timezone, plus the Ring-link rotation
// status section that surfaces token rotation health and a destructive
// "Force re-link" action gated behind <ForceRelinkModal> (type "RELINK"
// to confirm).

import { useEffect, useState } from "react";
import { ApiError } from "@/api";
import { tenantClient } from "@/api/tenantClient";
import { ForceRelinkModal } from "@/components/ForceRelinkModal";
import { useRole } from "@/role";
import type { RingLinkStatus, TenantDetails } from "@/types/tenant";

const TIMEZONES: string[] = [
  "America/Los_Angeles",
  "America/Denver",
  "America/Chicago",
  "America/New_York",
  "Europe/London",
  "Europe/Berlin",
  "Asia/Tokyo",
  "UTC",
];

function formatDate(iso: string | null): string {
  if (!iso) return "-";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString();
}

function rotationBadge(status: RingLinkStatus["rotation_state"]): JSX.Element {
  switch (status) {
    case "stable":
      return <span className="reb-badge reb-badge--success">Stable</span>;
    case "rotating":
      return <span className="reb-badge reb-badge--warning">Rotating</span>;
    case "dead":
      return <span className="reb-badge reb-badge--danger">Dead</span>;
  }
}

export function TenantSettingsPage(): JSX.Element {
  const { currentRole } = useRole();
  const [tenant, setTenant] = useState<TenantDetails | null>(null);
  const [link, setLink] = useState<RingLinkStatus | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [forbidden, setForbidden] = useState<boolean>(false);
  const [savingForm, setSavingForm] = useState<boolean>(false);
  const [formMessage, setFormMessage] = useState<string | null>(null);
  const [formDisplayName, setFormDisplayName] = useState<string>("");
  const [formTimezone, setFormTimezone] = useState<string>("UTC");
  const [relinkOpen, setRelinkOpen] = useState<boolean>(false);
  const [relinking, setRelinking] = useState<boolean>(false);

  useEffect(() => {
    if (currentRole === null) return;
    const ctrl = new AbortController();
    void (async () => {
      try {
        const [t, l] = await Promise.all([
          tenantClient.get(ctrl.signal),
          tenantClient.getRingLinkStatus(ctrl.signal),
        ]);
        setTenant(t);
        setLink(l);
        setFormDisplayName(t.display_name);
        setFormTimezone(t.timezone);
      } catch (err) {
        if (ctrl.signal.aborted) return;
        if (err instanceof ApiError && err.status === 403) {
          setForbidden(true);
          return;
        }
        const msg = err instanceof Error ? err.message : "Failed to load workspace";
        setError(msg);
      }
    })();
    return () => ctrl.abort();
  }, [currentRole]);

  async function handleSave(e: React.FormEvent): Promise<void> {
    e.preventDefault();
    if (!tenant) return;
    const trimmed = formDisplayName.trim();
    if (trimmed.length < 3 || trimmed.length > 80) {
      setFormMessage("Display name must be 3-80 characters.");
      return;
    }
    setSavingForm(true);
    setFormMessage(null);
    try {
      const updated = await tenantClient.update({
        display_name: trimmed,
        timezone: formTimezone,
      });
      setTenant(updated);
      setFormMessage("Saved.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Save failed";
      setFormMessage(msg);
    } finally {
      setSavingForm(false);
    }
  }

  async function handleRelink(): Promise<void> {
    setRelinking(true);
    try {
      await tenantClient.forceRelink();
      // Backend revokes session; redirect user to a neutral landing page.
      window.location.assign("/");
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Force re-link failed";
      setFormMessage(msg);
      setRelinking(false);
      setRelinkOpen(false);
    }
  }

  if (forbidden) {
    return (
      <div className="reb-page">
        <div className="reb-card reb-error">
          You do not have permission to view this page (requires{" "}
          <code>tenant:manage</code>).
        </div>
      </div>
    );
  }

  return (
    <div className="reb-page">
      <header className="reb-page__header">
        <h1 className="reb-page__title">Workspace Settings</h1>
      </header>

      {error && (
        <div className="reb-card reb-error" role="alert">
          {error}
        </div>
      )}

      {tenant && (
        <section className="reb-card" style={{ marginBottom: 24 }}>
          <h2 style={{ marginTop: 0 }}>Workspace details</h2>
          <form onSubmit={handleSave}>
            <div className="reb-form-row">
              <label htmlFor="reb-display-name" className="reb-form-row__label">
                Display name
              </label>
              <input
                id="reb-display-name"
                className="reb-input"
                value={formDisplayName}
                onChange={(e) => setFormDisplayName(e.target.value)}
                minLength={3}
                maxLength={80}
                disabled={savingForm}
              />
              <span className="reb-form-row__hint">3-80 characters.</span>
            </div>

            <div className="reb-form-row">
              <label htmlFor="reb-timezone" className="reb-form-row__label">
                Timezone
              </label>
              <select
                id="reb-timezone"
                className="reb-input"
                value={formTimezone}
                onChange={(e) => setFormTimezone(e.target.value)}
                disabled={savingForm}
              >
                {TIMEZONES.map((tz) => (
                  <option key={tz} value={tz}>
                    {tz}
                  </option>
                ))}
              </select>
            </div>

            <div className="reb-form-row">
              <span className="reb-form-row__label">Tenant ID</span>
              <code>{tenant.tenant_id}</code>
            </div>

            <div className="reb-form-row">
              <span className="reb-form-row__label">Admin alias</span>
              <code>{tenant.admin_alias}</code>
            </div>

            <div className="reb-form-row">
              <span className="reb-form-row__label">Status</span>
              <span>
                {tenant.status === "active" && (
                  <span className="reb-badge reb-badge--success">Active</span>
                )}
                {tenant.status === "pending_activation" && (
                  <span className="reb-badge reb-badge--warning">
                    Pending activation
                  </span>
                )}
                {tenant.status === "unlinked" && (
                  <span className="reb-badge reb-badge--danger">Unlinked</span>
                )}
              </span>
            </div>

            <button
              type="submit"
              className="reb-button reb-button--primary"
              disabled={savingForm}
            >
              {savingForm ? "Saving..." : "Save changes"}
            </button>
            {formMessage && (
              <span style={{ marginLeft: 12 }} role="status">
                {formMessage}
              </span>
            )}
          </form>
        </section>
      )}

      {link && (
        <section className="reb-card">
          <h2 style={{ marginTop: 0 }}>Ring Account Link</h2>
          <div className="reb-form-row">
            <span className="reb-form-row__label">Status</span>
            <span>{link.status}</span>
          </div>
          <div className="reb-form-row">
            <span className="reb-form-row__label">Rotation state</span>
            <span>{rotationBadge(link.rotation_state)}</span>
          </div>
          <div className="reb-form-row">
            <span className="reb-form-row__label">Last token rotation</span>
            <span>{formatDate(link.last_rotation_at)}</span>
          </div>
          <div className="reb-form-row">
            <span className="reb-form-row__label">Access token expires</span>
            <span>{formatDate(link.access_token_expires_at)}</span>
          </div>
          <button
            type="button"
            className="reb-button reb-button--danger"
            onClick={() => setRelinkOpen(true)}
            disabled={relinking}
          >
            Force re-link
          </button>
        </section>
      )}

      <ForceRelinkModal
        open={relinkOpen}
        busy={relinking}
        onConfirm={handleRelink}
        onCancel={() => setRelinkOpen(false)}
      />
    </div>
  );
}
